/** @module Camunda/DetectBPMN */
define([
	"dojo/_base/declare",
	"dojo/dom-class",
	"csi/action/Controller",
	"csi/comm/PostRequest",
	"csi/config",
	"csi/html",
	"csi/locale",
	"csi/reflection",
	"csi/store/Classes",
	"csi/widget/Dialog",
	"csi/widget/DialogForm",
	"csi/widget/ErrorText",
	"csi/widget/FilteringSelect",
	"csi/xml",
	"./main",
	"dojo/text!Camunda/html/ImportBPMNDialog.html",

	"csi/widget/SimpleTextWithTitle"
], function(declare,domClass,Controller,PostRequest,config,html,locale,reflection,ClassesStoreFactory,Dialog,DialogForm,ErrorText,FilteringSelect,xml,Camunda,dialogTemplate) {
	/**
	 * Create a store of all primary domain classes
	 */
	function createStore() {
		return ClassesStoreFactory(reflection.getClasses(),
			Camunda.isValidClass,
			function(rc, item) {
				item.name += ' (' + rc.getType() + ')';
			}
		);
	}

	const _ImportForm = declare(DialogForm, {
		templateString: dialogTemplate,

		/** @property {Widget} subtypeDropDown - Drop down for the original C8 sub type, located in the left pane */
		subtypeDropDown: null,

		/** @property {Widget} subtypeDropDown2 - Drop down for the new C8 sub type, located in the right pane */
		subtypeDropDown2: null,

		focus: ()=> {},

		/** @property {boolean} isAdding - set to true when the uploading file match the existing one */
		isAdding: false,

		/** @property {boolean} isApproved - set to true when the existing one already approved */
		isApproved: false,

		/** @property {boolean} hasApproved - set to true when the existing one has approved ones*/
		hasApproved: false,

		/** @property {Object} bpmnInformation - the Object holds all the information from the server */
		bpmnInformation: null,

		/** @property {boolean} supportSubtype - indicates if the existing c8 type support sub type or not */
		supportSubtype: false,

		/** @property {string} originalProcessId - the original process id in system if the uploaded file matches it,
		 * otherwise, represents the current id, which is brand new
		 **/
		originalProcessId: "",

		/** @property {string} newProcessId - the process id of the uploaded file */
		newProcessId: "",

		/** @property {string} operationType - either "New" or "Modify", indicates whether we add a new one or just modify the existing one */
		operationType: "",

		/** @property {string} introContent - the introduction text of the current context */
		introContent: "",

		/** @property {string} originalTitle - the title of the left pane(container of the brand new or the existing process) */
		originalTitle: "",

		/** @property {string} originalC8Type - the c8 type of the existing process(left pane) */
		originalC8Type: "",

		/** @property {string} originalC8SubType - the c8 sub type of the existing process(left pane) */
		originalC8SubType: "",

		/** @property {string} newC8Type - the c8 type of the uploading process(right pane) */
		newC8Type: "",

		/** @property {string} newC8SubType - the c8 sub type of the uploading process(right pane) */
		newC8SubType: "",

		/** @property {string} originalFileName - the file name of the existing process(left pane) */
		originalFileName: "",

		/** @property {string} newFileName - the file name of the uploading process(right pane) */
		newFileName: "",

		/** @property {string} originalNodeName - the node name of the existing process(left pane) */
		originalNodeName: "",

		/** @property {string} newNodeName - the node name of the uploading process(right pane) */
		newNodeName: "",

		/** @property {string} originalState - the state name of the existing process revision(left pane) */
		originalState: "",

		/** @property {string} newState - the state name of the uploading process revision(right pane) */
		newState: "",

		// DOMNode in view
		separatorNode: null,
		originalProcessIdNode: null,
		newProcessIdNode: null,
		operationTypeNode: null,
		introContentNode: null,
		originalTitleNode: null,
		originalC8PrimaryNode: null,
		newC8PrimaryNode: null,
		originalProcessFileNameNode: null,
		newProcessFileNameNode: null,
		originalProcessNameNode: null,
		newProcessNameNode: null,
		originalProcessStateNode: null,
		newProcessStateNode: null,

		_setOperationTypeAttr: {node: "operationTypeNode", type: "innerText"},
		_setIntroContentAttr: {node: "introContentNode", type: "innerText"},
		_setOriginalTitleAttr: {node: "originalTitleNode", type: "innerText"},
		_setOriginalC8TypeAttr: {node: "originalC8PrimaryNode", type: "innerText"},
		_setNewC8TypeAttr: {node: "newC8PrimaryNode", type: "innerText"},
		_setOriginalStateAttr: {node: "originalProcessStateNode", type: "innerText"},
		_setrNewStateAttr: {node: "newProcessStateNode", type: "innerText"},


		/**
		 * @override
		 * Initial the form based on the Object
		 */
		postCreate: function() {
			this._initiallizeC8TypeDropdown();
			this._initialForm(this.bpmnInformation);
		},

		/**
		 * Initial the form based on the Object
		 * @param {Object} bpmnInformation
		 */
		_initialForm: function(bpmnInformation) {
			const {
				hasApproved,
				hasMatch,
				isApproved,
				isDraft,
				newFileName,
				newNodeName,
				originalNodeName,
				originalFileName,
				primarySubtype,
				primaryType,
				processId,
				version
			} = bpmnInformation;

			const {
				originalC8SubNode,
				newC8SubNode,
				originalC8SubTextNode,
				newC8SubTextNode,
				originalProcessIdNode,
				originalProcessNameNode,
				originalProcessFileNameNode,
				originalProcessStateNode,
				operationTypeNode,
				separatorSectionNode,
				newSectionNode,
				originalC8SubNodeSection,
				newC8SubNodeSection,
			} = this;

			const isNew = !hasMatch;

			if (isNew) {
				// in the case of adding brand new process, just render the left pane
				domClass.toggle(operationTypeNode.parentNode, "csi-dialog-import-process-new");
				this.set("operationType", locale.resource("ImportWorkflowDialogNew"));

				// specify the content of introduction
				this.set("introContent", locale.resource("ImportWorkflowDialogNewIntro"));

				// don't show the title
				this.set("originalTitle", "");
				originalProcessIdNode.set("text", processId);

				originalProcessNameNode.set("text", newNodeName);
				originalProcessFileNameNode.set("text", newFileName);

				//don't show the state of the process
				html.setVisible(originalProcessStateNode, false);
			} else {
				// the top section start
				this.supportSubtype = !!config.getSubtypeStore(primaryType);
				domClass.toggle(operationTypeNode.parentNode, "csi-dialog-import-process-existing");
				this.set("operationType", locale.resource("ImportWorkflowDialogModify"));

				// specify the content of introduction
				this.set("introContent", locale.resource("ImportWorkflowDialogExistingIntro"));

				html.setVisible(separatorSectionNode, true);
				html.setVisible(newSectionNode, true);

				// the left pane start
				this.set("originalTitle", locale.resource("ImportWorkflowDialogExisting"));
				originalProcessIdNode.set("text", processId);

				originalProcessNameNode.set("text", originalNodeName);
				originalProcessFileNameNode.set("text", originalFileName);

				if (isDraft) {
					domClass.toggle(originalProcessStateNode, "csi-state-camundaprocessdefinitionrevision-draft");
					this.set("originalState", locale.resource("Draft"));
				}

				if (isApproved) {
					domClass.toggle(originalProcessStateNode, "csi-state-camundaprocessdefinitionrevision-published");
					this.set("originalState", locale.resource("CamundaVersionLastPublished", [version]));
				}

				this.set("originalC8Type", primaryType);

				// show the subtype as well if any
				if (this.supportSubtype) {
					html.setVisible(originalC8SubNodeSection, true);
					html.setVisible(originalC8SubNode, !primarySubtype);
					html.setVisible(originalC8SubTextNode, !!primarySubtype);
					originalC8SubTextNode.set("text", primarySubtype);
				}

				// the right pane start
				this.newProcessIdNode.set("text", processId);
				this.newProcessNameNode.set("text", newNodeName);
				this.newProcessFileNameNode.set("text", newFileName);

				if (isApproved || hasApproved) {
					this.set("newC8Type", primaryType);

					if (this.supportSubtype) {
						html.setVisible(newC8SubNodeSection, true);
						html.setVisible(newC8SubNode, !primarySubtype);
						html.setVisible(newC8SubTextNode, !!primarySubtype);
						newC8SubTextNode.set("text", primarySubtype);
					}
				}
			}
		},

		/**
		 * initialize the drop down based on the state of operation
		 */
		_initiallizeC8TypeDropdown: function() {
			let {isAdding, isApproved, hasApproved} = this;
			isApproved = isApproved || !!hasApproved;

			let name;
			let container;

			if (isAdding) {
				name = "originalPrimaryType";
				container = this.originalC8PrimaryNode;
			} else if (isApproved === false) {
				name = "newPrimaryType";
				container = this.newC8PrimaryNode;
			}

			const dropdown = new FilteringSelect({
				store: createStore(),
				required: true,
				customErrorHandling: true,
				placeHolder: locale.resource("Select"),
				value: "",
				name,
				onChange: this._initializeSubtype
			});

			if (isAdding) {
				this.primaryTypeDropDown = dropdown;
			} else if (isApproved === false) {
				this.primaryTypeDropDown2 = dropdown;
			}

			// in the case of finding a published process matches the uploading one, will be replaced by the pure text anyway, no need to render
			if (container) {
				dropdown.placeAt(container);
				dropdown.set("value", this.bpmnInformation.primaryType);

				// handle the error
				new ErrorText({
					className: "csi-error-message",
					inputWidget: dropdown
				}).placeAt(container);
			}
		},

		/**
		 * initialize the sub type drop down based on the primary type
		 */
		_initializeSubtype: function(value) {
			const store = config.getSubtypeStore(value);
			const bCurrentTypeSupportSubtype = !!store;
			const form = this.getParent();

			if (!form) {
				return;
			}

			const c8SubtypeSection = form.newC8SubNodeSection;
			const c8SubtypeNode = form.newC8SubNode;

			const c8OriginalSubtypeSection = form.originalC8SubNodeSection;
			const c8OriginalSubtypeNode = form.originalC8SubNode;

			const forOriginal = (this.name == "originalPrimaryType");
			let dropdown = forOriginal? this.subtypeDropDown : this.subtypeDropDown2;

			if (bCurrentTypeSupportSubtype) {

				if (!dropdown) {
					dropdown = new FilteringSelect({
						store,
						required: true,
						customErrorHandling: true,
						placeHolder: locale.resource("Select"),
						value: "",
						name: forOriginal ? "originalSubType": "newSubType"
					});

					dropdown.placeAt(forOriginal ? c8OriginalSubtypeNode : c8SubtypeNode);

					if (forOriginal) {
						this.subtypeDropDown = dropdown;
					} else {
						this.subtypeDropDown2 = dropdown;
					}
				}

				dropdown.set("store", store);
				dropdown.set("value", form.bpmnInformation.primarySubtypeURL || "");

				// only apply to the dropdown the first time, avoid unexpected value when user change the primary type
				form.bpmnInformation.primarySubtypeURL = "";
			} else {
				// the selected primary doesn't support sub type, just hide it, clear the value avoid sending to the server
				if (dropdown) {
					dropdown.set("value", "");
				}
			}

			html.setVisible(c8OriginalSubtypeSection, (forOriginal && bCurrentTypeSupportSubtype) || form.supportSubtype);
			html.setVisible(c8SubtypeSection, bCurrentTypeSupportSubtype && !forOriginal);
		},

		/**
		 * @override
		 * the drop down takes over the validation
		 */
		validate: function() {
			if (this.isAdding) {
				return this.primaryTypeDropDown.validate();
			} else if (!this.isApproved) {
				return this.primaryTypeDropDown2.validate();
			}

			return true;
		}
	});

	return declare(Controller, {
		// Set to true so that controller is stopped on next action when user cancels file browser */
		bInterrupt: true,

		/** @property {boolean} isAdding - set to true when the uploading file match the existing one */
		isAdding: false,

		/** @property {boolean} isApproved - set to true when the existing one already approved */
		isApproved: false,

		/** @property {boolean} hasApproved - set to true when the existing one has any approved ones */
		hasApproved: false,

		/** @property {Object} bpmnInformation - the Object holds all the information from the server */
		bpmnInformation: null,

		/** @property {Object} value - the Object holds all the information on the submit-form */
		value: null,

		getStringParameters: function() {
			return [locale.type("CamundaProcessDefinition")];
		},

		/**
		 * @override
		 * Create the form and pop up the dialog, suspend the process as well
		 */
		preProcess: function() {
			const bpmnForm = new _ImportForm({
				bpmnInformation: this.bpmnInformation,
				isAdding: this.isAdding,
				isApproved: this.isApproved,
				hasApproved: this.hasApproved
			});

			const dlg = this.dialog = new Dialog({
				form: bpmnForm,
				dialogActions: true,
				destroyOnHide: true,
				subjectNode: null,
				title: locale.resource("ImportWorkflowDialogTitle"),
				onSave: (value) => this._onSave(value),
				_hasValueChanged: (v1, newValue) => {
					// In the case of published revision, there's no values need to be specified on the form, still need to send
					// the data to server though, so, always return true here
					return true;
				}
			});

			dlg.startup();
			dlg.show();
			this.suspendProcess();
		},

		/**
		 * @override
		 * Select files to upload and get the information from the server
		 */
		onPreActionDeferreds: function(dfds) {
			const self = this;
			const req = new PostRequest("Camunda", "DetectBPMN");

			dfds.push(html.selectFile().then((file) => {
				req.addFiles(file, "SourceFile");
				return req.post({});
			}).then((response) => {
				const bpmnInformation = self.bpmnInformation = response.getResultNode();
				self.isAdding = !(bpmnInformation.hasMatch);
				self.isApproved = bpmnInformation.isApproved;
				self.hasApproved = bpmnInformation.hasApprovedRevision;
			}));
		},

		/**
		 * @override
		 * Refresh the table after process successfully
		 */
		postProcessSuccess: function(response) {
			this.widget.refreshWidget();
		},

		/**
		 * @override
		 * Publish the changes to server
		 */
		toWriter: function() {
			const value = this.value;

			if (value) {
				const call = this.m_writer.callMethod("Camunda", "UploadBPMNFromString");
				let primaryType = "";
				let subTypeUrl = "";

				if (this.isAdding) {
					primaryType = value.originalPrimaryType;
					subTypeUrl = value.originalSubType;
				} else if (!this.isApproved) {
					primaryType = value.newPrimaryType;
					subTypeUrl = value.newSubType;
				}

				call.addParameter("BPMN20XMLString", xml.ATTR_STRING, this.bpmnInformation.BPMN20XML);
				call.addParameter("PrimaryType", xml.ATTR_STRING, primaryType);
				call.addParameter("SubType", xml.ATTR_STRING, subTypeUrl);
				call.addParameter("FileName", xml.ATTR_STRING, this.bpmnInformation.newFileName);

				return call;
			}

			return null;
		},

		/**
		 * resume the action and proceed
		 */
		_onSave: function(value) {
			this.value = value;
			this.resumeProcess();
			this.process();
			this.dialog.hide();
		}
	});
});